<template>
  <div class="p-6 mx-auto max-w-7xl space-y-6">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-2xl font-bold">Админ — Аналитика AI-рекомендаций</h1>
        <div v-if="clinicId" class="mt-1 text-sm text-gray-600">
          Фильтр: клиника ID {{ clinicId }}
          <button class="ml-2 underline" @click="clearClinicFilter">сбросить</button>
        </div>
      </div>

      <button
        class="inline-flex items-center gap-2 px-4 py-2 rounded-xl border hover:bg-gray-50"
        @click="fetchData"
        :disabled="loading"
      >
        <svg v-if="loading" class="animate-spin h-4 w-4" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"/>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/>
        </svg>
        <span>Обновить</span>
      </button>
    </div>

    <!-- Фильтры -->
    <div class="border rounded-2xl p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3 items-end bg-white">
      <div class="flex flex-col gap-1">
        <span class="text-sm text-gray-600">Клиника</span>
        <select v-model="clinicId" class="rounded-xl border px-3 py-2">
          <option value="">Все клиники</option>
          <option v-for="c in clinics" :key="c.id" :value="String(c.id)">{{ c.name }}</option>
        </select>
      </div>

      <div class="flex flex-col gap-1">
        <span class="text-sm text-gray-600">Период</span>
        <select v-model="period" class="rounded-xl border px-3 py-2" @change="onPeriodChange">
          <option value="today">Сегодня</option>
          <option value="yesterday">Вчера</option>
          <option value="last_7_days">Последние 7 дней</option>
          <option value="this_month">Этот месяц</option>
          <option value="custom">Свои дни (N)</option>
          <option value="">Произвольные даты</option>
        </select>
      </div>

      <div v-if="period === 'custom'" class="flex flex-col gap-1">
        <span class="text-sm text-gray-600">Дней (1–90)</span>
        <input type="number" min="1" max="90" v-model.number="days" class="rounded-xl border px-3 py-2" placeholder="напр., 30" />
      </div>

      <template v-if="period === ''">
        <div class="flex flex-col gap-1">
          <span class="text-sm text-gray-600">От (YYYY-MM-DD)</span>
          <input type="date" v-model="from" class="rounded-xl border px-3 py-2" />
        </div>
        <div class="flex flex-col gap-1">
          <span class="text-sm text-gray-600">До (YYYY-MM-DD)</span>
          <input type="date" v-model="to" class="rounded-xl border px-3 py-2" />
        </div>
      </template>

      <div class="flex gap-2 mt-2 lg:mt-0">
        <button class="px-4 py-2 rounded-xl bg-black text-white" @click="fetchData" :disabled="loading">Применить</button>
        <button class="px-4 py-2 rounded-xl border" @click="resetAll">Сброс</button>
      </div>
    </div>

    <!-- Ошибка -->
    <div v-if="error" class="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
      Ошибка: {{ error }}
    </div>

    <!-- Статы -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
      <Stat label="Всего использований" :value="totalUsage" />
      <Stat label="МКБ" :value="byMkb" />
      <Stat label="Анамнез" :value="byPrompt" />
      <Stat label="Шаблон" :value="byTemplate" />
      <Stat label="Клиник в выборке" :value="clinicRows.length" />
    </div>

    <div v-if="templateWarning" class="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700">
      Статистика по шаблону временно недоступна: {{ templateWarning }}
    </div>

    <!-- Табы -->
    <div>
      <div class="inline-flex rounded-xl border bg-white p-1">
        <button :class="tabBtn('clinics')" @click="activeTab = 'clinics'">По клиникам</button>
        <button :class="tabBtn('doctors')" @click="activeTab = 'doctors'">Врачи</button>
        <button :class="tabBtn('types')" @click="activeTab = 'types'">Типы запросов</button>
      </div>

      <!-- По клиникам -->
      <div v-if="activeTab === 'clinics'" class="mt-4 space-y-4">
        <div class="rounded-2xl border bg-white p-4">
          <div class="h-80">
            <VChart :option="clinicOption" autoresize @click="onClinicBarClick" />
          </div>
          <div class="text-sm text-gray-500 mt-2">
            Показаны клиники по убыванию количества запросов
            <span v-if="clinicId">| выбран фильтр по клинике: {{ clinicId }}</span>
          </div>
        </div>

        <div class="overflow-auto rounded-2xl border bg-white">
          <table class="min-w-full text-sm">
            <thead class="bg-gray-50 sticky top-0">
              <tr>
                <th class="text-left p-3">Клиника</th>
                <th class="text-right p-3">Врачей</th>
                <th class="text-right p-3">МКБ</th>
                <th class="text-right p-3">Анамнез</th>
                <th class="text-right p-3">Шаблон</th>
                <th class="text-right p-3">Итого</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, idx) in clinicRows"
                :key="idx"
                :class="['border-t hover:bg-gray-50', row.clinic_id ? 'cursor-pointer' : 'cursor-default']"
                @click="handleClinicClick(row)"
                :title="row.clinic_id ? 'Открыть врачей клиники' : 'Нет clinic_id для перехода'"
              >
                <td class="p-3">{{ row.name }}</td>
                <td class="p-3 text-right">{{ row.doctor_count ?? '—' }}</td>
                <td class="p-3 text-right">{{ row.mkb }}</td>
                <td class="p-3 text-right">{{ row.anamnesis }}</td>
                <td class="p-3 text-right">{{ row.template_count }}</td>
                <td class="p-3 text-right font-medium">{{ row.total }}</td>
              </tr>
              <tr v-if="clinicRows.length === 0">
                <td class="p-3 text-gray-500" colspan="6">Нет данных</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Врачи -->
      <div v-if="activeTab === 'doctors'" class="mt-4 space-y-4">
        <!-- Гистограмма топ-15 -->
        <div class="rounded-2xl border bg-white p-4">
          <div class="h-80">
            <VChart :option="doctorOption" autoresize />
          </div>
        </div>

        <!-- Поиск/сортировка -->
        <div class="rounded-2xl border bg-white p-4 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
          <div class="flex items-center gap-2">
            <input
              v-model.trim="doctorSearch"
              type="text"
              class="rounded-xl border px-3 py-2 w-72"
              placeholder="Поиск врача/категории/клиники…"
            />
            <button class="px-3 py-2 rounded-xl border" @click="doctorSearch = ''">Сброс</button>
          </div>

          <div class="flex items-center gap-2">
            <span class="text-sm text-gray-600">Сортировка:</span>
            <select v-model="doctorSortBy" class="rounded-xl border px-3 py-2">
              <option value="total">Итого</option>
              <option value="mkb_count">МКБ</option>
              <option value="anamnesis_count">Анамнез</option>
              <option value="template_count">Шаблон</option>
              <option value="doctor_name">ФИО</option>
            </select>
            <button class="px-3 py-2 rounded-xl border" @click="doctorSortDesc = !doctorSortDesc">
              {{ doctorSortDesc ? '↓ убыв.' : '↑ возр.' }}
            </button>
          </div>
        </div>

        <!-- Таблица врачей (все, с пагинацией) -->
        <div class="overflow-auto rounded-2xl border bg-white">
          <table class="min-w-full text-sm">
            <thead class="bg-gray-50 sticky top-0">
              <tr>
                <th class="text-left p-3">Врач</th>
                <th class="text-left p-3">Клиника</th>
                <th class="text-left p-3">Категория</th>
                <th class="text-right p-3">МКБ</th>
                <th class="text-right p-3">Анамнез</th>
                <th class="text-right p-3">Шаблон</th>
                <th class="text-right p-3">Итого</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(d, idx) in doctorsPaged" :key="idx" class="border-t hover:bg-gray-50">
                <td class="p-3">{{ d.doctor_name }}</td>
                <td class="p-3">{{ d.clinic_name }}</td>
                <td class="p-3">{{ d.category_name || '—' }}</td>
                <td class="p-3 text-right">{{ d.mkb_count }}</td>
                <td class="p-3 text-right">{{ d.anamnesis_count }}</td>
                <td class="p-3 text-right">{{ d.template_count }}</td>
                <td class="p-3 text-right font-medium">{{ d.total }}</td>
              </tr>
              <tr v-if="doctorsAll.length === 0">
                <td class="p-3 text-gray-500" colspan="7">Нет данных</td>
              </tr>
            </tbody>
          </table>

          <!-- Пагинация -->
          <div class="flex items-center justify-between p-3 text-sm">
            <div>
              Показано {{ Math.min(doctorPageEnd, doctorsAll.length) }} из {{ doctorsAll.length }}
            </div>
            <div class="flex items-center gap-2">
              <button class="px-3 py-1 rounded border" :disabled="doctorPage === 1" @click="doctorPage--">Назад</button>
              <span>Стр. {{ doctorPage }} / {{ doctorTotalPages }}</span>
              <button class="px-3 py-1 rounded border" :disabled="doctorPage === doctorTotalPages" @click="doctorPage++">Вперёд</button>
              <select v-model.number="doctorPageSize" class="rounded border px-2 py-1">
                <option :value="10">10</option>
                <option :value="20">20</option>
                <option :value="50">50</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <!-- Типы запросов -->
      <div v-if="activeTab === 'types'" class="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div class="rounded-2xl border bg-white p-4">
          <div class="h-72">
            <VChart :option="typeOption" autoresize />
          </div>
        </div>

        <div class="rounded-2xl border bg-white p-4 space-y-2 text-sm text-gray-700">
          <h3 class="font-semibold">Диапазон</h3>
          <div>
            <template v-if="data?.range?.period">
              Период: <b>{{ data.range.period }}</b>
              <template v-if="data.range.days"> — {{ data.range.days }} дн.</template>
            </template>
            <template v-else>
              От: <b>{{ data?.range?.from ?? '—' }}</b> до <b>{{ data?.range?.to ?? '—' }}</b>
            </template>
          </div>
          <div>Фильтр клиники: <b>{{ data?.filters?.clinic_id ?? 'все' }}</b></div>
          <div>
            Шаблонный диапазон:
            <template v-if="templateStats.filtered_created_at_from || templateStats.filtered_created_at_to">
              От: <b>{{ templateStats.filtered_created_at_from ?? '—' }}</b>
              до <b>{{ templateStats.filtered_created_at_to ?? '—' }}</b>
            </template>
            <template v-else>—</template>
          </div>
          <div>
            Шаблонная клиника:
            <b>{{ templateStats.filtered_clinic_name ?? 'все' }}</b>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import adminService from '@/services/adminService'
import { pmspService } from '@/services/pmspService'
import apiClient from '@/axios'

/* ECharts */
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { BarChart, PieChart } from 'echarts/charts'
import { GridComponent, TooltipComponent, LegendComponent, TitleComponent } from 'echarts/components'
import VChart from 'vue-echarts'
use([CanvasRenderer, BarChart, PieChart, GridComponent, TooltipComponent, LegendComponent, TitleComponent])

/* Stat card */
const Stat = {
  props: { label: String, value: [String, Number] },
  template: `
    <div class="rounded-2xl bg-white shadow-sm border p-4 flex flex-col gap-1">
      <span class="text-sm text-gray-500">{{ label }}</span>
      <span class="text-2xl font-semibold">{{ value }}</span>
    </div>
  `,
}

/* state */
const loading = ref(false)
const error = ref(null)
const data = ref(null)
const clinics = ref([])
const templateStats = ref(createEmptyTemplateStats())
const templateWarning = ref(null)

/* filters */
const clinicId = ref('')
const period = ref('this_month')
const days = ref('')
const from = ref('')
const to = ref('')

/* tabs */
const activeTab = ref('clinics')
const tabBtn = (which) =>
  ['px-4 py-2 rounded-lg text-sm', activeTab.value === which ? 'bg-black text-white' : 'text-gray-700 hover:bg-gray-50'].join(' ')

/* API */
async function fetchClinics() {
  try {
    const { data: res } = await apiClient.get('/clinics', { params: { per_page: 200 } })
    const items = Array.isArray(res?.data) ? res.data : res
    clinics.value = (items || []).map(c => ({ id: c.id, name: c.name }))
  } catch (e) { /* no-op */ }
}

async function fetchData() {
  loading.value = true
  error.value = null
  templateWarning.value = null
  try {
    const params = {
      clinic_id: clinicId.value || undefined,
      period: period.value || undefined,
      days: period.value === 'custom' ? (days.value || undefined) : undefined,
      from: period.value ? undefined : (from.value || undefined),
      to:   period.value ? undefined : (to.value || undefined),
    }

    const selectedClinicName = resolveSelectedClinicName()
    const templateDateParams = buildTemplateDateParams()
    const templateParams = {
      clinic_name: selectedClinicName || undefined,
      period: period.value || undefined,
      days: period.value === 'custom' ? (days.value || undefined) : undefined,
      from: templateDateParams.from,
      to: templateDateParams.to,
      created_at_from: templateDateParams.created_at_from,
      created_at_to: templateDateParams.created_at_to,
    }

    const [analyticsResult, templateResult] = await Promise.allSettled([
      adminService.getAnalytics(params),
      pmspService.getTemplateUsageStats(templateParams),
    ])

    if (analyticsResult.status === 'rejected') {
      throw analyticsResult.reason
    }

    data.value = analyticsResult.value

    if (templateResult.status === 'fulfilled') {
      templateStats.value = normalizeTemplateStats(templateResult.value)
    } else {
      templateStats.value = createEmptyTemplateStats()
      templateWarning.value = templateResult.reason?.message || 'Не удалось загрузить статистику по шаблону'
    }

    // сброс страницы при новом запросе
    doctorPage.value = 1
  } catch (e) {
    error.value = e?.message || 'Ошибка загрузки'
  } finally {
    loading.value = false
  }
}

function resetDates() { from.value = ''; to.value = ''; days.value = '' }
function resetAll() { clinicId.value = ''; period.value = 'this_month'; resetDates(); fetchData() }
function clearClinicFilter() { clinicId.value = ''; fetchData(); activeTab.value = 'clinics' }
function onPeriodChange() {
  resetDates()
  if (period.value && period.value !== 'custom') {
    fetchData()
  }
}

function formatLocalDate(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function addDays(date, amount) {
  const next = new Date(date)
  next.setDate(next.getDate() + amount)
  return next
}

function buildTemplateDateParams() {
  const today = new Date()
  let rangeFrom
  let rangeTo

  if (period.value === '') {
    rangeFrom = from.value || undefined
    rangeTo = to.value || undefined
  } else if (period.value === 'yesterday') {
    const yesterday = addDays(today, -1)
    const value = formatLocalDate(yesterday)
    rangeFrom = value
    rangeTo = value
  } else if (period.value === 'today') {
    const value = formatLocalDate(today)
    rangeFrom = value
    rangeTo = value
  } else if (period.value === 'last_7_days') {
    rangeFrom = formatLocalDate(addDays(today, -6))
    rangeTo = formatLocalDate(today)
  } else if (period.value === 'this_month') {
    rangeFrom = formatLocalDate(new Date(today.getFullYear(), today.getMonth(), 1))
    rangeTo = formatLocalDate(today)
  } else if (period.value === 'custom') {
    const daysCount = Number(days.value)
    if (Number.isFinite(daysCount) && daysCount > 0) {
      rangeFrom = formatLocalDate(addDays(today, -(daysCount - 1)))
      rangeTo = formatLocalDate(today)
    }
  } else {
    rangeFrom = formatLocalDate(new Date(today.getFullYear(), today.getMonth(), 1))
    rangeTo = formatLocalDate(today)
  }

  if (rangeFrom && rangeTo && rangeFrom > rangeTo) {
    const tmp = rangeFrom
    rangeFrom = rangeTo
    rangeTo = tmp
  }

  return {
    from: rangeFrom,
    to: rangeTo,
    created_at_from: rangeFrom,
    created_at_to: rangeTo,
  }
}

onMounted(async () => {
  await fetchClinics()
  await fetchData()
})

function createEmptyTemplateStats() {
  return {
    filtered_clinic_name: null,
    filtered_created_at_from: null,
    filtered_created_at_to: null,
    total_usage_count: 0,
    by_doctor: [],
    by_clinic: [],
  }
}

function normalizeTemplateStats(payload) {
  return {
    filtered_clinic_name: payload?.filtered_clinic_name ?? null,
    filtered_created_at_from: payload?.filtered_created_at_from ?? null,
    filtered_created_at_to: payload?.filtered_created_at_to ?? null,
    total_usage_count: payload?.total_usage_count ?? 0,
    by_doctor: Array.isArray(payload?.by_doctor) ? payload.by_doctor : [],
    by_clinic: Array.isArray(payload?.by_clinic) ? payload.by_clinic : [],
  }
}

function normalizeKey(value) {
  return String(value ?? '').trim().toLocaleLowerCase('ru-RU')
}

function getClinicMapKey({ clinic_id, clinic_name }, fallback = 'unknown-clinic') {
  return normalizeKey(clinic_name) || (clinic_id != null ? `clinic:${clinic_id}` : fallback)
}

function getDoctorMapKey({ doctor_name, clinic_name }, fallback = 'unknown-doctor') {
  const normalizedDoctor = normalizeKey(doctor_name)
  const normalizedClinic = normalizeKey(clinic_name)
  return `${normalizedDoctor || fallback}::${normalizedClinic || 'no-clinic'}`
}

function resolveSelectedClinicName() {
  if (!clinicId.value) return undefined

  return clinics.value.find(c => String(c.id) === clinicId.value)?.name
    || data.value?.by_clinic?.find(c => String(c.clinic_id) === clinicId.value)?.clinic_name
}

/* computed: totals */
const totalUsage = computed(() => (data.value?.total ?? 0) + templateStats.value.total_usage_count)
const byMkb = computed(() => data.value?.by_type?.find(t => t.type === 'by_mkb')?.total ?? 0)
const byPrompt = computed(() => data.value?.by_type?.find(t => t.type === 'by_prompt')?.total ?? 0)
const byTemplate = computed(() => templateStats.value.total_usage_count)

/* clinics table rows */
const clinicRows = computed(() => {
  if (!data.value) return []
  const rowsMap = new Map()

  for (const [index, clinic] of (data.value.by_clinic || []).entries()) {
    const baseTotal = clinic.total ?? ((clinic.mkb_count ?? 0) + (clinic.anamnesis_count ?? 0))
    rowsMap.set(getClinicMapKey(clinic, `analytics-clinic:${index}`), {
      clinic_id: clinic.clinic_id,
      name: clinic.clinic_name || 'Без клиники',
      doctor_count: clinic.doctor_count,
      mkb: clinic.mkb_count ?? 0,
      anamnesis: clinic.anamnesis_count ?? 0,
      template_count: 0,
      base_total: baseTotal,
      total: baseTotal,
    })
  }

  for (const [index, templateClinic] of templateStats.value.by_clinic.entries()) {
    const templateCount = templateClinic.usage_count ?? 0
    const key = getClinicMapKey(templateClinic, `template-clinic:${index}`)
    const existing = rowsMap.get(key)

    if (existing) {
      existing.template_count = templateCount
      existing.total = existing.base_total + templateCount
      continue
    }

    rowsMap.set(key, {
      clinic_id: null,
      name: templateClinic.clinic_name || 'Без клиники',
      doctor_count: undefined,
      mkb: 0,
      anamnesis: 0,
      template_count: templateCount,
      base_total: 0,
      total: templateCount,
    })
  }

  return Array.from(rowsMap.values()).sort((a, b) => {
    if (b.total !== a.total) return b.total - a.total
    return String(a.name).localeCompare(String(b.name), 'ru')
  })
})

/* drilldown from clinic to doctors */
function handleClinicClick(row) {
  if (!row?.clinic_id) return
  clinicId.value = String(row.clinic_id)
  activeTab.value = 'doctors'
  fetchData()
}

function onClinicBarClick(params) {
  const row = clinicRows.value.find(r => r.name === params.name)
  if (row?.clinic_id) handleClinicClick(row)
}

/* doctors: search/sort/pagination */
const doctorSearch = ref('')
const doctorSortBy = ref('total')
const doctorSortDesc = ref(true)
const doctorPage = ref(1)
const doctorPageSize = ref(20)

const doctorsAll = computed(() => {
  if (!data.value) return []
  const rowsMap = new Map()

  for (const [index, doctor] of (data.value.by_doctor || []).entries()) {
    const baseTotal = doctor.total ?? ((doctor.mkb_count ?? 0) + (doctor.anamnesis_count ?? 0))
    rowsMap.set(getDoctorMapKey(doctor, `analytics-doctor:${index}`), {
      ...doctor,
      clinic_name: doctor.clinic_name || '—',
      mkb_count: doctor.mkb_count ?? 0,
      anamnesis_count: doctor.anamnesis_count ?? 0,
      template_count: 0,
      base_total: baseTotal,
      total: baseTotal,
    })
  }

  for (const [index, templateDoctor] of templateStats.value.by_doctor.entries()) {
    const templateCount = templateDoctor.usage_count ?? 0
    const key = getDoctorMapKey(templateDoctor, `template-doctor:${index}`)
    const existing = rowsMap.get(key)

    if (existing) {
      existing.template_count = templateCount
      existing.total = existing.base_total + templateCount
      if ((!existing.clinic_name || existing.clinic_name === '—') && templateDoctor.clinic_name) {
        existing.clinic_name = templateDoctor.clinic_name
      }
      continue
    }

    rowsMap.set(key, {
      doctor_name: templateDoctor.doctor_name || 'Без имени',
      clinic_name: templateDoctor.clinic_name || '—',
      category_name: null,
      mkb_count: 0,
      anamnesis_count: 0,
      template_count: templateCount,
      base_total: 0,
      total: templateCount,
    })
  }

  let rows = Array.from(rowsMap.values())

  // search
  const q = doctorSearch.value.trim().toLowerCase()
  if (q) {
    rows = rows.filter(d =>
      (d.doctor_name || '').toLowerCase().includes(q) ||
      (d.clinic_name || '').toLowerCase().includes(q) ||
      (d.category_name || '').toLowerCase().includes(q)
    )
  }

  // sort
  const key = doctorSortBy.value
  rows.sort((a, b) => {
    const av = a[key] ?? 0
    const bv = b[key] ?? 0
    if (typeof av === 'number' && typeof bv === 'number') return doctorSortDesc.value ? (bv - av) : (av - bv)
    return doctorSortDesc.value ? String(bv).localeCompare(String(av)) : String(av).localeCompare(String(bv))
  })

  return rows
})

const doctorTotalPages = computed(() => Math.max(1, Math.ceil(doctorsAll.value.length / doctorPageSize.value)))
const doctorPageStart = computed(() => (doctorPage.value - 1) * doctorPageSize.value)
const doctorPageEnd = computed(() => doctorPageStart.value + doctorPageSize.value)
const doctorsPaged = computed(() => doctorsAll.value.slice(doctorPageStart.value, doctorPageEnd.value))

watch([doctorSearch, doctorSortBy, doctorSortDesc, doctorPageSize], () => { doctorPage.value = 1 })

/* charts */
const clinicOption = computed(() => {
  const rows = clinicRows.value
  return {
    tooltip: { trigger: 'axis' },
    legend: { data: ['МКБ', 'Анамнез', 'Шаблон'] },
    grid: { left: 8, right: 8, top: 24, bottom: 8, containLabel: true },
    xAxis: { type: 'category', data: rows.map(r => r.name), axisLabel: { show: false } },
    yAxis: { type: 'value' },
    series: [
      { name: 'МКБ', type: 'bar', stack: 'total', data: rows.map(r => r.mkb) },
      { name: 'Анамнез', type: 'bar', stack: 'total', data: rows.map(r => r.anamnesis) },
      { name: 'Шаблон', type: 'bar', stack: 'total', data: rows.map(r => r.template_count) },
    ],
  }
})

const doctorsTopForChart = computed(() => doctorsAll.value.slice(0, 15))
const doctorOption = computed(() => {
  const rows = doctorsTopForChart.value
  return {
    tooltip: { trigger: 'axis' },
    legend: { data: ['МКБ', 'Анамнез', 'Шаблон'] },
    grid: { left: 8, right: 8, top: 24, bottom: 8, containLabel: true },
    xAxis: { type: 'category', data: rows.map(r => r.doctor_name), axisLabel: { show: false } },
    yAxis: { type: 'value' },
    series: [
      { name: 'МКБ', type: 'bar', data: rows.map(r => r.mkb_count) },
      { name: 'Анамнез', type: 'bar', data: rows.map(r => r.anamnesis_count) },
      { name: 'Шаблон', type: 'bar', data: rows.map(r => r.template_count) },
    ],
  }
})

const typeOption = computed(() => {
  const pie = [
    { name: 'МКБ', value: byMkb.value },
    { name: 'Анамнез', value: byPrompt.value },
    { name: 'Шаблон', value: byTemplate.value },
  ].filter(item => item.value > 0)
  return {
    tooltip: { trigger: 'item' },
    legend: { orient: 'horizontal', bottom: 0 },
    series: [
      {
        name: 'Тип запроса',
        type: 'pie',
        radius: ['40%', '70%'],
        avoidLabelOverlap: true,
        label: { show: true, formatter: '{b}: {c}' },
        data: pie,
      },
    ],
  }
})
</script>

<style scoped>
/* при необходимости — доп. стили */
</style>
